/* eslint-disable complexity */
import { css } from 'emotion';
import React, { useState, useEffect } from 'react';

import { getCurrencySymbol } from '@eventbrite/intl';
import AlertChunkySvg from '../../components/lean-eds/iconography/icons/AlertChunky';
import SectionFloatingDialog from '../../components/lean-eds/maps/floating-dialog/section/section-floating-dialog';
import ModalManageHolds from '../../components/lean-eds/maps/modal-manage-holds';
import Zoom from '../../components/lean-eds/maps/zoom';
import {
    TYPE_ERROR,
    TYPE_SUCCESS,
} from '../../components/lean-eds/notification/constants';
import NotificationBar from '../../components/lean-eds/notification/NotificationBar';
import InlineAlert from '../../components/lean-eds/inline-alert/InlineAlert';
import { gettext } from '@eventbrite/i18n';
import CheckoutWidget from '../../components/checkout-widget';
import ThreeViewer from '../../components/three-viewer';
import { VIEW_MODE_MANAGE_DASHBOARD } from '../../constants/global-constants';
import { PAYMENT_OPTIONS } from '../../constants/payments-constants';
import webglDetectHelper from '../../helpers/webgl-detect-helper';
import {
    MANAGE_CHECKOUT_PANEL_WIDTH,
    MANAGE_FOOTER_HEIGHT,
    MANAGE_HEADER_AND_FOOTER_Z_INDEX,
    MANAGE_HEADER_HEIGHT,
} from './constants';
import ManageDashboard from './manage-dashboard';
import {
    getCheckoutWidgetParentUrl,
    getVariantsNamesLabel,
    goToReportsPage,
    parseSectionFloatingDialogSectionInfo,
    parseSectionFloatingDialogTiers,
    parseSectionHoldsVariants,
} from './utils';

const MapTemplate = ({ container }) => {
    const {
        displaySeatInfoMessage,
        closeInfoMessage,
        clearErrorMessage,
        clearSuccessMessage,
        getErrorMessage,
        getEventData,
        getTicketSelection,
        getHoverSectionInfo,
        getGASectionVariants,
        getViewMode,
        getChairRadiusInPixels,
        handleSelectGATickets,
        handleSelectTicket,
        off,
        on,
        sceneInterface,
        getZoomButtons,
        getNotifications,
        handleGotoSection,
        hasSelectedGATickets,
        idsBelongToGASection,
        getOrderInfo,
        removeCartItem,
        setAddonQuantity,
        setOrderSummaryTicketPrice,
        syncCart,
        getTotalUnitsInfo,
        resetSelectedGATickets,
        setSelectedGASectionVariantQuantity,
        getSyncCartStatus,
        hasItemsCarted,
        getLegendDialogVisibility,
        toggleLegendDialogVisibility,
        getTiers,
        getCheckoutModalVisibility,
        setCheckoutModalVisibility,
        setPaymentInstruction,
        getPaymentInstruction,
        getOrderPriceInfo,
        getManageHoldsModalVisibility,
        setManageHoldsModalVisibility,
        showManageHoldsModal,
        getSectionHoldsInfo,
        getSuccessMessage,
        getSectionTotalUnitsInfo,
        setSectionHoldQuantity,
        saveSectionHolds,
        getSectionTiersData,
        savePaymentInstruction,
        getEventId,
        getCartId,
        getCheckoutWidgetVisibility,
        setCheckoutWidgetVisibility,
        getPrintDialogVisibility,
        togglePrintDialogVisibility,
        getPDF,
        getPrintOptionValue,
        setPrintOptionValue,
        getSoldSeatInfo,
        getSoldSeatInfoFetched,
        releaseHold,
        getHeldSeatIsReleasing,
    } = container;
    const zoomButtons = getZoomButtons();
    const chairRadiusInPixels = getChairRadiusInPixels();
    const orderInfo = getOrderInfo();
    const {
        seatInfo = {},
        variants = [],
        position: ticketSelectionPosition,
        visible: isTicketSelectionVisible,
        useArrow: ticketSelectionUseArrow,
        hasHolds,
        ids: ticketSelectionIds,
        isSold,
    } = getTicketSelection();

    const ticketPositionType = ticketSelectionUseArrow ? 'arrow' : 'corner';

    const isWebGLPresent = webglDetectHelper.isWebGLPresent();

    const errorMessage = getErrorMessage();
    const successMessage = getSuccessMessage();
    const {
        getIsAnimating,
        getScene,
        getViewBox,
        getViewport,
        seatmapMouseDown: onMouseDown,
        seatmapMouseMove: onMouseMove,
        seatmapMouseUp: onMouseUp,
        seatmapWheel: onWheel,
        setViewport,
    } = sceneInterface;

    const [shouldShowInfoMessage, setShouldShowInfoMessage] = useState(false);

    useEffect(() => {
        setShouldShowInfoMessage(displaySeatInfoMessage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const infoMessageComponent = (
        <div
            className={css({
                width: '90%',
                position: 'absolute',
                bottom: '15px',
                left: '50%',
                transform: 'translate(-50%, 0)',
            })}
        >
            <InlineAlert
                data-testid="seat-info-message"
                iconType={<AlertChunkySvg />}
                title={gettext(
                    'Want to change your selected seats?',
                ).toString()}
                description={gettext(
                    'You need to deselect your current seat or eliminate it to select a new one.',
                ).toString()}
                showCloseButton={true}
                type="neutral"
                closeHandler={() => {
                    closeInfoMessage();
                    setShouldShowInfoMessage(false);
                }}
            />
        </div>
    );

    if (!isWebGLPresent) {
        return (
            <div className={css({ height: '100%', width: '100%' })}>
                <NotificationBar
                    hasCloseButton={false}
                    iconType={<AlertChunkySvg />}
                    type={TYPE_ERROR}
                >
                    {gettext(
                        "Seat map isn't compatible with your internet browser. Try updating your browser or use a different browser",
                    )}
                </NotificationBar>
            </div>
        );
    }

    const viewMode = getViewMode();
    const viewport = getViewport();

    let header;
    let footer;
    let checkoutPanel;
    let zoomComponent;
    let notifications;
    let threeViewer;
    let ticketSelectionComponent;
    let sectionInfoComponent;
    let errorMessageComponent;
    let checkoutModal;
    let manageHoldsModal;
    let successMessageComponent;
    let checkoutWidget;

    if (viewMode === VIEW_MODE_MANAGE_DASHBOARD) {
        const eventData = getEventData();
        const totalUnitsInfo = getTotalUnitsInfo();
        const cartIsSyncing = getSyncCartStatus();
        const hoverSectionInfo = {
            ...(getHoverSectionInfo() || {}),
            isDisplay: true,
        };
        // We will only show the section info if we are in a far view
        const sectionInfoVisible = hoverSectionInfo.farView;
        const manageHoldsModalVisible = getManageHoldsModalVisibility();
        const checkoutWidgetVisibility = getCheckoutWidgetVisibility();
        const heldSeatIsReleasing = getHeldSeatIsReleasing();
        const eventId = getEventId();

        header = ManageDashboard.getHeader({
            eventData,
            totalUnitsInfo,
        });

        notifications = ManageDashboard.getNotifications({
            notifications: getNotifications(),
        });

        zoomComponent = (
            <div
                className={css({
                    position: 'absolute',
                    top: `${MANAGE_HEADER_HEIGHT}px`,
                    zIndex: MANAGE_HEADER_AND_FOOTER_Z_INDEX,
                })}
            >
                <Zoom buttons={zoomButtons} />
            </div>
        );

        checkoutPanel = ManageDashboard.getCheckout({
            orderInfo,
            removeTicket: removeCartItem,
            setAddonQuantity,
            setOrderSummaryTicketPrice,
            syncCart,
            isLoading: cartIsSyncing,
            currencySymbol: getCurrencySymbol(eventData.currency),
        });

        const margins = {
            top: MANAGE_HEADER_HEIGHT,
            bottom: MANAGE_FOOTER_HEIGHT,
            left: 0,
            right: checkoutPanel ? MANAGE_CHECKOUT_PANEL_WIDTH : 0,
        };

        threeViewer = (
            <ThreeViewer
                autoAdjustSize={true}
                getSizeFromParent={true}
                interactive={true}
                sceneInterface={{
                    getIsAnimating,
                    getScene,
                    onMouseDown,
                    onMouseUp,
                    onMouseMove,
                    onWheel,
                    getViewBox,
                    getViewport,
                    setViewport,
                    on,
                    off,
                }}
                margins={margins}
            />
        );

        const showTicketSelectionComponent =
            isTicketSelectionVisible &&
            !manageHoldsModalVisible &&
            !heldSeatIsReleasing;
        const ticketSelectionBelongsToGASection =
            ticketSelectionIds &&
            ticketSelectionIds.length > 0 &&
            idsBelongToGASection({
                ids: ticketSelectionIds,
            });

        ticketSelectionComponent =
            showTicketSelectionComponent &&
            ManageDashboard.getTicketSelectionComponent({
                ticketSelectionBelongsToGASection,
                chairRadiusInPixels,
                getSectionTotalUnitsInfo,
                getGASectionVariants,
                handleManageHolds: showManageHoldsModal,
                handleSelectGATickets,
                handleSelectTicket,
                hasHolds,
                hasSelectedGATickets,
                isSold,
                margins,
                onReleaseAction: releaseHold,
                seatInfo,
                setSelectedGASectionVariantQuantity,
                soldSeatInfo: getSoldSeatInfo(),
                soldSeatInfoFetched: getSoldSeatInfoFetched(),
                ticketPositionType,
                ticketSelectionPosition,
                variants,
                viewMode,
                viewport,
                resetSelectedGATickets,
            });

        if (
            sectionInfoVisible &&
            !showTicketSelectionComponent &&
            !heldSeatIsReleasing
        ) {
            const { hoverSectionId, position } = hoverSectionInfo;
            const totalUnitsInfo = getSectionTotalUnitsInfo(hoverSectionId);
            const sectionTiers = getSectionTiersData(hoverSectionId);

            sectionInfoComponent = (
                <SectionFloatingDialog
                    section={parseSectionFloatingDialogSectionInfo({
                        totalUnitsInfo,
                        hoverSectionInfo,
                    })}
                    tiers={parseSectionFloatingDialogTiers(sectionTiers)}
                    position={position}
                    viewport={viewport}
                    margins={margins}
                    positionType="corner"
                    handleGoToSection={() => handleGotoSection(hoverSectionId)}
                />
            );
        }

        errorMessageComponent = errorMessage ? (
            <div
                key="error-notification"
                className={css({
                    position: 'absolute',
                    bottom: `${MANAGE_FOOTER_HEIGHT + 24}px`,
                    right: checkoutPanel
                        ? `${MANAGE_CHECKOUT_PANEL_WIDTH + 24}px`
                        : '24px',
                })}
            >
                <NotificationBar
                    showCloseButton={true}
                    closeHandler={clearErrorMessage}
                    type={TYPE_ERROR}
                >
                    {errorMessage}
                </NotificationBar>
            </div>
        ) : undefined;

        successMessageComponent = successMessage ? (
            <div
                key="success-notification"
                className={css({
                    position: 'absolute',
                    bottom: `${MANAGE_FOOTER_HEIGHT + 24}px`,
                    right: checkoutPanel
                        ? `${MANAGE_CHECKOUT_PANEL_WIDTH + 24}px`
                        : '24px',
                })}
            >
                <NotificationBar
                    closeHandler={clearSuccessMessage}
                    type={TYPE_SUCCESS}
                >
                    {successMessage}
                </NotificationBar>
            </div>
        ) : undefined;

        checkoutModal =
            getCheckoutModalVisibility() &&
            ManageDashboard.getCheckoutModal({
                handleClose: () => {
                    setCheckoutModalVisibility(false);
                },
                onPrimaryAction: savePaymentInstruction,
                paymentInfo: {
                    handleSelector: (value) =>
                        setPaymentInstruction({
                            paymentStatus: value,
                        }),
                    onBlur: () => false,
                    paymentOptions: PAYMENT_OPTIONS,
                    selectedValue: getPaymentInstruction(),
                },
                priceInfo: getOrderPriceInfo(),
                disablePrimaryAction: !getPaymentInstruction(),
            });

        checkoutWidget = checkoutWidgetVisibility && (
            <CheckoutWidget
                cartId={getCartId()}
                parentUrl={getCheckoutWidgetParentUrl()}
                eventId={eventId}
                onClose={() => setCheckoutWidgetVisibility(false)}
                onOrderComplete={({ orderId }) => {
                    goToReportsPage({
                        eventId,
                        orderId,
                    });
                }}
            />
        );

        if (getManageHoldsModalVisibility()) {
            const { sectionId, sectionLabel } = seatInfo;
            const {
                available: unitsAvailable,
                total: unitsTotal,
                totalHolds,
                sold: totalSold,
            } = getSectionTotalUnitsInfo(sectionId);
            const {
                holds: sectionHoldsVariants,
                isDirty: sectionHoldsIsDirty,
            } = getSectionHoldsInfo();

            manageHoldsModal = (
                <ModalManageHolds
                    isShown={true}
                    handleClose={() => setManageHoldsModalVisibility(false)}
                    variants={parseSectionHoldsVariants(sectionHoldsVariants)}
                    onHeldQuantityChange={(holdId, quantity) =>
                        setSectionHoldQuantity({
                            holdId,
                            quantity,
                        })
                    }
                    Info={{
                        title: sectionLabel,
                        ticketType: getVariantsNamesLabel(variants),
                        availableQuantityLabel: gettext(
                            '%(unitsAvailable)s/%(unitsTotal)s',
                            { unitsAvailable, unitsTotal },
                        ).toString(),
                        totalHolds: `${totalHolds}`,
                    }}
                    onPrimaryAction={saveSectionHolds}
                    disablePrimaryAction={!sectionHoldsIsDirty}
                />
            );
        }

        footer = ManageDashboard.getFooter({
            isTicketSelectionVisible,
            isDisabled: !hasItemsCarted() || cartIsSyncing,
            legendDialogIsVisible: getLegendDialogVisibility(),
            toggleLegendDialogVisibility,
            tiers: getTiers(),
            getTicketsAction: setCheckoutModalVisibility,
            printDialogIsVisible: getPrintDialogVisibility(),
            togglePrintDialogVisibility,
            eventData,
            getPDF,
            getPrintOptionValue,
            setPrintOptionValue,
        });
    } else {
        threeViewer = (
            <ThreeViewer
                autoAdjustSize={true}
                getSizeFromParent={true}
                interactive={true}
                sceneInterface={{
                    getIsAnimating,
                    getScene,
                    onMouseDown,
                    onMouseUp,
                    onMouseMove,
                    onWheel,
                    getViewBox,
                    getViewport,
                    setViewport,
                    on,
                    off,
                }}
                margins={{}}
            />
        );

        zoomComponent = <Zoom buttons={zoomButtons} />;

        ticketSelectionComponent =
            isTicketSelectionVisible &&
            ManageDashboard.getTicketSelectionComponent({
                viewMode,
                seatInfo,
                handleSelectTicket,
                ticketSelectionPosition,
                ticketPositionType,
                hasHolds,
                hasSelectedGATickets,
                onReleaseAction: () => false,
                variants,
                viewport,
                chairRadiusInPixels,
            });

        errorMessageComponent = errorMessage ? (
            <div
                className="eds-g-cell-10-12"
                style={{
                    position: 'absolute',
                    bottom: '80px',
                    right: '24px',
                }}
            >
                <NotificationBar
                    showCloseButton={true}
                    iconType={<AlertChunkySvg />}
                    closeHandler={clearErrorMessage}
                    type={TYPE_ERROR}
                >
                    {errorMessage}
                </NotificationBar>
            </div>
        ) : undefined;
    }

    return (
        <div
            className={css({
                height: '100%',
                width: '100%',
                display: 'grid',
                gridTemplateRows: 'auto',
            })}
        >
            <div
                className={css({
                    height: '100vh',
                    overflow: 'hidden',
                    position: 'relative',
                })}
            >
                {header}
                {zoomComponent}
                {notifications}
                {errorMessageComponent}
                {successMessageComponent}
                {shouldShowInfoMessage && infoMessageComponent}
                {threeViewer}
                {ticketSelectionComponent}
                {sectionInfoComponent}
                {checkoutPanel}
                {checkoutModal}
                {checkoutWidget}
                {manageHoldsModal}
                {footer}
            </div>
        </div>
    );
};

export default MapTemplate;
